import React, { useState, useEffect } from 'react';
import { getDashboard } from '../services/api';
import StatsCards from '../components/StatsCards';
import { formatUSD, formatSOL } from '../utils/format';

function Dashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboard();
    const interval = setInterval(loadDashboard, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const data = await getDashboard();
      setDashboard(data);
      setError(null);
    } catch (err) {
      // Handle empty vault scenario gracefully
      if (err.response?.status === 500 || err.message?.includes('No active vault wallets')) {
        setDashboard({
          treasury_balance_lamports: 0,
          treasury_balance_sol: 0,
          treasury_balance_usd: null,
          sol_price_usd: null,
          today_stats: { total_bets: 0, total_income: 0, total_payout: 0, net_profit: 0, total_wins: 0, total_losses: 0, win_rate: 0 },
          week_stats: { total_bets: 0, total_income: 0, total_payout: 0, net_profit: 0, total_wins: 0, total_losses: 0, win_rate: 0 },
          month_stats: { total_bets: 0, total_income: 0, total_payout: 0, net_profit: 0, total_wins: 0, total_losses: 0, win_rate: 0 },
          all_time_stats: { total_bets: 0, total_income: 0, total_payout: 0, net_profit: 0, total_wins: 0, total_losses: 0, win_rate: 0 },
          wallets: [],
          is_testnet: true
        });
        setError(null); // Clear error, show empty state instead
      } else {
        setError(err.response?.data?.detail || 'Failed to load dashboard');
        console.error('Dashboard error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="bg-zinc-900 border border-red-600 rounded-lg p-6 max-w-md">
          <h3 className="text-red-400 font-bold text-lg mb-2">Error</h3>
          <p className="text-gray-300">{error}</p>
          <button onClick={loadDashboard} className="mt-4 bg-orange-500 text-black px-4 py-2 rounded hover:bg-orange-400 font-medium">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check if vault is empty (no wallets)
  const isVaultEmpty = dashboard && dashboard.wallets && dashboard.wallets.length === 0;

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800 shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">🔐 Admin Dashboard</h1>
              <p className="text-gray-400 mt-1">
                Solana Dice Game Management
                {dashboard?.is_testnet && <span className="ml-2 text-xs bg-orange-900/50 text-orange-400 px-2 py-1 rounded">TESTNET</span>}
              </p>
            </div>
            <button onClick={loadDashboard} className="bg-orange-500 text-black px-4 py-2 rounded-lg hover:bg-orange-400 flex items-center gap-2 font-medium">
              <span>🔄</span> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Empty Vault Warning */}
      {isVaultEmpty && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-zinc-900 border-2 border-orange-600 rounded-lg p-8 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-orange-400 mb-2">Vault is Empty</h2>
            <p className="text-gray-400 mb-6">
              No wallet addresses found. You need to generate vault wallets before the system can operate.
            </p>
            <div className="bg-zinc-800 rounded-lg p-6 max-w-2xl mx-auto text-left">
              <h3 className="font-semibold text-white mb-3">Quick Setup:</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-300">
                <li>Run the wallet generation script in the backend</li>
                <li>Or use the Wallet Management section below to create wallets manually</li>
                <li>Recommended: Generate wallets for 2x, 3x, 5x, 10x, and 100x multipliers</li>
              </ol>
              <div className="mt-4 p-4 bg-black rounded border border-zinc-600">
                <p className="text-sm font-mono text-orange-400">
                  cd backend<br/>
                  python generate_wallets.py
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!isVaultEmpty && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Treasury Balance */}
          <div className="bg-gradient-to-r from-orange-600 to-orange-800 rounded-lg shadow-lg p-8 text-white mb-8">
            <h2 className="text-xl font-semibold mb-4">💰 Total Treasury Balance</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-orange-200 text-sm">Solana</p>
                <p className="text-4xl font-bold">{formatSOL(dashboard.treasury_balance_sol ?? dashboard.treasury_balance_btc ?? 0)} SOL</p>
              </div>
              <div>
                <p className="text-orange-200 text-sm">USD Value</p>
                <p className="text-4xl font-bold">
                  {dashboard.is_testnet ? 'N/A (Testnet)' : formatUSD(dashboard.treasury_balance_usd)}
                </p>
              </div>
              <div>
                <p className="text-orange-200 text-sm">SOL Price</p>
                <p className="text-2xl font-semibold">
                  {dashboard.is_testnet ? 'N/A (Testnet)' : formatUSD(dashboard.sol_price_usd ?? dashboard.btc_price_usd)}
                </p>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <StatsCards dashboard={dashboard} />
        </div>
      )}

    </div>
  );
}

export default Dashboard;
